import { useState, useEffect, useRef, useMemo } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  supabase,
  asLexiconSelectChain,
  type LexiconSelectQuery,
} from "@/integrations/supabase/client";
import { createTermMultipart } from "@/lib/termsApi";
import { fetchProfileRemote, getStoredAccessToken } from "@/lib/authApi";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import { CheckCircle, Send, AlertTriangle, LogIn } from "lucide-react";
import { AudioUploadField } from "@/admin/components/AudioUploadField";

function buildContributeSchema(
  t: (key: string, vars?: Record<string, string | number>) => string
) {
  return z
    .object({
      word: z.string().min(1, t("form.validate.wordRequired")).max(100),
      meaningEnglish: z.string().max(2000).optional(),
      meaningHindi: z.string().max(2000).optional(),
      meaningGujarati: z.string().max(2000).optional(),
      pronunciation: z.string().max(500).optional(),
      exampleUsage: z.string().max(2000).optional(),
      contributorName: z
        .string()
        .trim()
        .min(1, t("form.validate.nameRequired"))
        .max(100),
      contributorEmail: z
        .string()
        .min(1, t("form.validate.emailRequired"))
        .email(t("form.validate.emailInvalid")),
      isFeatured: z.boolean(),
    })
    .refine(
      data =>
        Boolean(
          data.meaningEnglish?.trim() ||
            data.meaningHindi?.trim() ||
            data.meaningGujarati?.trim()
        ),
      {
        message: t("form.validate.oneMeaning"),
        path: ["meaningEnglish"],
      }
    );
}

type FormData = z.infer<ReturnType<typeof buildContributeSchema>>;

type ContributeFormProps = {
  variant?: "page" | "dialog";
  onContributionSuccess?: () => void;
};

function ContributeFormInner({
  variant = "page",
  onContributionSuccess,
}: ContributeFormProps) {
  const { t } = useLanguage();
  const formSchema = useMemo(() => buildContributeSchema(t), [t]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const identityDefaultsRef = useRef({ name: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [existingMatch, setExistingMatch] = useState<{
    id: string;
    word: string;
  } | null>(null);
  const [wordSuggestions, setWordSuggestions] = useState<
    { id: string; word: string }[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wordWrapperRef = useRef<HTMLDivElement>(null);

  const mergedResolver = useMemo<Resolver<FormData>>(
    () => (values, context, options) => {
      const id = identityDefaultsRef.current;
      const merged: FormData = {
        ...values,
        contributorName:
          id.name.trim() ||
          (typeof values.contributorName === "string"
            ? values.contributorName.trim()
            : "") ||
          "",
        contributorEmail:
          id.email.trim() ||
          (typeof values.contributorEmail === "string"
            ? values.contributorEmail.trim()
            : "") ||
          "",
      };
      return zodResolver(formSchema)(merged, context, options);
    },
    [formSchema]
  );

  useEffect(() => {
    if (!user) {
      return;
    }
    const checkActive = async () => {
      const { data } = await asLexiconSelectChain(
        supabase.from("profiles").select("is_active")
      )
        .eq("user_id", user.id)
        .maybeSingle();
      const row = data as { is_active?: boolean } | null;
      if (row && row.is_active === false) {
        setIsDeactivated(true);
      }
    };
    checkActive();
  }, [user]);

  const form = useForm<FormData>({
    resolver: mergedResolver,
    defaultValues: {
      word: "",
      meaningEnglish: "",
      meaningHindi: "",
      meaningGujarati: "",
      pronunciation: "",
      exampleUsage: "",
      contributorName: "",
      contributorEmail: "",
      isFeatured: false,
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    const fallbackEmail = user.email?.trim() ?? "";
    const atIdx = fallbackEmail.indexOf("@");
    const localPart =
      atIdx > 0
        ? fallbackEmail.slice(0, atIdx).replace(/\./g, " ").trim()
        : fallbackEmail.trim();
    const fallbackName =
      localPart.length > 0
        ? localPart
        : fallbackEmail.trim() || "Contributor";

    const apply = (email: string, name: string) => {
      const e = email.trim() || fallbackEmail;
      const n = name.trim() || fallbackName;
      identityDefaultsRef.current = { email: e, name: n };
      form.setValue("contributorEmail", e);
      form.setValue("contributorName", n);
    };

    apply(fallbackEmail, fallbackName);

    let cancelled = false;
    void (async () => {
      const { profile } = await fetchProfileRemote(user.id, "portal");
      if (cancelled) {
        return;
      }
      const email = profile?.email?.trim() || fallbackEmail;
      const fromParts = [profile?.first_name, profile?.last_name]
        .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
        .map(x => x.trim())
        .join(" ")
        .trim();
      const composed =
        (profile?.full_name?.trim() || fromParts || "").trim();
      const name =
        composed.length > 0 ? composed : fallbackName;
      apply(email, name);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form instance stable; run when user id/email changes
  }, [user?.id, user?.email]);

  const wordValue = form.watch("word");

  useEffect(() => {
    const checkExisting = async () => {
      if (wordValue.trim().length < 2) {
        setExistingMatch(null);
        setWordSuggestions([]);
        return;
      }
      const chain = (
        supabase.from("terms").select("id, word") as unknown as LexiconSelectQuery
      )
        .eq("status", "approved")
        .ilike("word", `%${wordValue.trim()}%`)
        .limit(5);
      const { data } = await Promise.resolve(
        chain as unknown as Promise<{ data: unknown; error: unknown }>
      );
      const matches = Array.isArray(data)
        ? (data as { id: string; word: string }[])
        : [];
      setWordSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      const exact = matches.find(
        (m: { id: string; word: string }) =>
          m.word.toLowerCase() === wordValue.trim().toLowerCase()
      );
      setExistingMatch(exact || null);
    };
    const debounce = setTimeout(checkExisting, 400);
    return () => clearTimeout(debounce);
  }, [wordValue]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wordWrapperRef.current &&
        !wordWrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) {
    return (
      <Card className="border-border/50 mx-auto max-w-2xl shadow-xl">
        <CardContent className="py-16 text-center">
          <div className="bg-primary/10 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full">
            <LogIn className="text-primary h-8 w-8" />
          </div>
          <h3 className="text-foreground mb-2 font-serif text-2xl font-semibold">
            {t("form.loginRequired")}
          </h3>
          <p className="text-muted-foreground mb-6">
            {t("form.loginRequiredBody")}
          </p>
          <Link to="/login">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <LogIn className="mr-2 h-5 w-5" />
              {t("form.loginRegisterButton")}
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (isDeactivated) {
    return (
      <Card className="border-border/50 mx-auto max-w-2xl shadow-xl">
        <CardContent className="py-16 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <LogIn className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-foreground mb-2 font-serif text-2xl font-semibold">
            {t("form.accountDeactivated")}
          </h3>
          <p className="text-muted-foreground mb-6">
            {t("form.accountDeactivatedBody")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const resetFormDefaults = (): FormData => {
    const id = identityDefaultsRef.current;
    return {
      word: "",
      meaningEnglish: "",
      meaningHindi: "",
      meaningGujarati: "",
      pronunciation: "",
      exampleUsage: "",
      contributorName: id.name,
      contributorEmail: id.email,
      isFeatured: false,
    };
  };

  const onSubmit = async (data: FormData) => {
    const token = getStoredAccessToken();
    if (!token) {
      toastApiError(t("form.sessionExpired"));
      return;
    }

    const idSnap = identityDefaultsRef.current;
    const contributorName = idSnap.name.trim();
    const contributorEmail = idSnap.email.trim();
    if (!contributorName || !contributorEmail) {
      toastApiError(t("form.profileLoadFailed"));
      return;
    }

    setIsSubmitting(true);

    const { ok, error } = await createTermMultipart(
      {
        word: data.word.trim(),
        meaning_english: data.meaningEnglish?.trim() ?? "",
        meaning_hindi: data.meaningHindi?.trim() ?? "",
        meaning_gujarati: data.meaningGujarati?.trim() ?? "",
        pronunciation: data.pronunciation?.trim() ?? "",
        example_usage: data.exampleUsage?.trim() ?? "",
        status: "pending",
        contributor_name: contributorName,
        contributor_email: contributorEmail,
        is_featured: data.isFeatured,
      },
      audioFile
    );

    setIsSubmitting(false);

    if (!ok || error) {
      toastApiError(error || t("form.submitFailed"));
      return;
    }

    setAudioFile(null);
    toast.success(t("contribute.success"));

    if (onContributionSuccess) {
      form.reset(resetFormDefaults());
      onContributionSuccess();
      return;
    }

    setIsSubmitted(true);
  };

  if (variant === "page" && isSubmitted) {
    return (
      <Card className="border-border/50 shadow-card mx-auto max-w-2xl">
        <CardContent className="py-16 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-foreground mb-2 font-serif text-2xl font-semibold">
            {t("contribute.success")}
          </h3>
          <p className="text-muted-foreground mb-6">
            {t("contribute.successReviewBody")}
          </p>
          <div className="flex justify-center gap-3">
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setAudioFile(null);
                form.reset(resetFormDefaults());
              }}
              variant="outline"
            >
              {t("contribute.submitAnother")}
            </Button>
            <Link to="/contribute#contributions">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                {t("contribute.viewMyContributions")}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "border-border/50 mx-auto max-w-2xl shadow-xl",
        variant === "dialog" &&
          "mx-0 max-w-none border-0 bg-transparent shadow-none"
      )}
    >
      <CardHeader
        className={cn("pb-2 text-center", variant === "dialog" && "pb-4 pt-0")}
      >
        <CardTitle
          className={cn(
            "font-serif text-2xl",
            variant === "dialog" && "text-xl leading-snug md:text-2xl"
          )}
        >
          {t("contribute.title")}
        </CardTitle>
        <CardDescription
          className={cn("text-base", variant === "dialog" && "text-sm")}
        >
          {t("contribute.description")}
        </CardDescription>
        <p
          className={cn(
            "text-muted-foreground mt-2 text-sm",
            variant === "dialog" && "text-xs sm:text-sm"
          )}
        >
          {t("contribute.submittingAs")}{" "}
          <span className="text-foreground font-medium">{user.email}</span>
        </p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div ref={wordWrapperRef} className="relative">
              <FormField
                control={form.control}
                name="word"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      {t("contribute.word")} *
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("contribute.wordPlaceholder")}
                        className="bg-card border-border/60"
                        onFocus={() =>
                          wordSuggestions.length > 0 && setShowSuggestions(true)
                        }
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {showSuggestions && wordSuggestions.length > 0 && (
                <div className="border-border/50 absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border bg-white shadow-xl">
                  <p className="text-muted-foreground border-border/30 bg-secondary/30 border-b px-4 py-2 text-xs font-semibold uppercase tracking-wider">
                    {t("form.existingTerms")}
                  </p>
                  {wordSuggestions.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => navigate(`/term/${s.id}`)}
                      className="hover:bg-primary/5 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
                    >
                      <span className="text-foreground font-medium">
                        {s.word}
                      </span>
                      <span className="text-primary text-xs">
                        {t("form.viewTerm")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {existingMatch && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {t("form.termAlreadyExists", {
                      word: existingMatch.word,
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/term/${existingMatch.id}`)}
                    className="text-primary mt-1 text-sm hover:underline"
                  >
                    {t("form.viewExistingTerm")}
                  </button>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="meaningEnglish"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    {t("contrib.colMeaningEn")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t(
                        "contribute.meaningEnglishPlaceholder"
                      )}
                      className="bg-card border-border/60 min-h-[100px] text-base md:min-h-[120px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 md:grid-cols-2 md:gap-6">
              <FormField
                control={form.control}
                name="meaningHindi"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      {t("contribute.meaningHindi")}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="हिंदी में अर्थ लिखें..."
                        className="bg-card border-border/60 font-devanagari min-h-[100px] text-base md:min-h-[120px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="meaningGujarati"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      {t("contribute.meaningGujarati")}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="ગુજરાતીમાં અર્થ લખો..."
                        className="bg-card border-border/60 font-devanagari min-h-[100px] text-base md:min-h-[120px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pronunciation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    {t("contribute.pronunciation")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("contribute.pronunciationPlaceholder")}
                      className="bg-card border-border/60"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="exampleUsage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    {t("contribute.example")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("contribute.examplePlaceholder")}
                      className="bg-card border-border/60 min-h-[90px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            

            <AudioUploadField
              pendingFile={audioFile}
              onPendingFileChange={setAudioFile}
              busy={isSubmitting}
            />

            <div className="border-border/50 border-t pt-6">
              <p className="text-muted-foreground mb-1 text-sm">
                {t("contribute.yourInfo")}
              </p>
              <p className="text-muted-foreground mb-4 text-xs">
                {t("contribute.identityFromProfile")}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="contributorName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">
                        {t("contribute.name")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled
                          placeholder={t("contribute.name")}
                          autoComplete="name"
                          className={cn(
                            "bg-muted/35 border-border/60 opacity-95",
                            "disabled:cursor-not-allowed disabled:opacity-95"
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contributorEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">
                        {t("contribute.contributorEmail")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          disabled
                          placeholder={t("contribute.email")}
                          autoComplete="email"
                          className={cn(
                            "bg-muted/35 border-border/60 opacity-95",
                            "disabled:cursor-not-allowed disabled:opacity-95"
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer h-12 w-full text-base"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  {t("form.submitting")}
                </span>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  {t("contribute.submit")}
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export function ContributeForm(props: ContributeFormProps = {}) {
  const { language } = useLanguage();
  return <ContributeFormInner key={language} {...props} />;
}
